import { useEffect } from 'react';
import { warnOnce } from '@/lib/warnOnce';

/**
 * Scales the whole UI down until the document fits the screen. **This is the fix for QA-10.**
 *
 * ## The defect
 *
 * `docs/QA-PARITY.md` QA-10: on the 1080×1920 viewport every display actually has, `szeged_2`
 * renders a 2013px document, `szeged_3` 2219px and `szeged_4` 2287px. `body` scrolls
 * (`overflow: auto`, MIGRATION decision #7) so nothing clips — but nothing scrolls a signage
 * display either, so 93px, 299px and 367px of those pages are simply not on the wall: the
 * app-store badges on all three, and the clock and date row on two. It is identical in the
 * legacy app. Nobody had ever run either app at the size of the screen it runs on.
 *
 * ## Why scaling, and not row distribution
 *
 * The obvious CSS answer — give the widget grid a definite height and let it distribute rows —
 * does not work, and it is worth being precise about why: **constraining a row does not shrink
 * what is inside it.** Every dimension in this app is `rem`, so a widget's content height is
 * whatever the root font size says it is; a shorter grid row just means the content overflows
 * the row instead of the page. Worse, `align-content` defaults to `stretch`, so a definite
 * height would *spread* the four configs that already fit their screen across it — a visible
 * change to the control group, in the opposite direction from the defect.
 *
 * What does shrink rem-sized content is a smaller root font size, and applying it at the root
 * shrinks everything by the same factor. That is the graceful squeeze the brief asks for: a
 * page 19% too tall renders 19% smaller, in proportion, with nothing clipped, nothing
 * ellipsised and nothing selectively crushed. `szeged_4` needs ~0.84 — the same decision a
 * person makes when they say "make it a bit smaller so it fits".
 *
 * ## Why every pass starts from the design scale
 *
 * `.signage-container` is `min-height: 100dvh`, so once a page fits, `scrollHeight` is pinned
 * to the viewport and says nothing about how much room is left. A hook that measured its own
 * scaled output would therefore only ever learn that it should shrink further — and a display
 * runs for weeks, so a widget that is briefly tall would ratchet the whole screen down for
 * good.
 *
 * So each pass removes the factor, measures the page at its intended size, and computes the
 * factor from that. All of it happens inside one animation frame with no `await` between the
 * write and the read, so the browser never paints the intermediate state: the reset costs a
 * forced reflow, not a flash.
 *
 * ## Why a factor and not a font size
 *
 * The root font size is a proportion of the viewport width (`global.css`, "Root font size"),
 * and that is the app's *design* scale — the thing that makes a 4K panel render at the same
 * physical size as a 1080 one. This hook must not overwrite it, only multiply it, or the two
 * mechanisms fight over one declaration. Hence `--signage-fit`, which the `html` rule reads.
 */

/** The multiplier the `html` rule reads. Absent means 1, which is the common case. */
const FIT_PROPERTY = '--signage-fit';

/**
 * The floor. A config needing more than a 30% reduction is not a page that wants squeezing —
 * it is a config with too much on it, and shrinking further trades one unreadable screen for
 * another. At the floor the hook stops and says which numbers it gave up on, which is
 * something a person can act on; the content below the fold is then the same defect as before,
 * reported rather than silently crushed.
 */
export const MIN_FIT = 0.7;

/**
 * How much of the available height to aim for. The 0.2% headroom absorbs the sub-pixel
 * rounding between "the height a layout computes" and "the integer `scrollHeight` reports" —
 * without it a page can settle one pixel over the fold, which is invisible on a wall and a
 * failing assertion in `scripts/qa-screenshots.ts`.
 */
const FIT_HEADROOM = 0.998;

/**
 * Hysteresis: a factor within 0.3% of the applied one is not worth a write. This is what keeps
 * a page that fits *exactly* — `corio` and `hbr_tcc_2/3` are clamped to the viewport height to
 * the pixel — from writing an inline style on every measurement.
 */
const FIT_TOLERANCE = 0.003;

/**
 * Verification passes after the first. Height is very nearly linear in the root font size, so
 * one pass lands within a percent; the extra two are for the parts that are not (a few px
 * constants, image intrinsic sizes, a line that rewraps).
 */
const MAX_PASSES = 3;

export type FitInputs = {
  /** Full document height at the design scale — `documentElement.scrollHeight`. */
  contentPx: number;
  /** Height the screen can show — `documentElement.clientHeight`. */
  availablePx: number;
};

/**
 * The factor that makes `contentPx` fit `availablePx`: `available / content`, with headroom,
 * clamped to `[MIN_FIT, 1]` and rounded *down* to three decimals so rounding can only ever
 * shrink.
 *
 * Never more than 1 — the design scale is the intended size and this hook only shrinks — and
 * `undefined` when the inputs cannot support an answer, which in practice means jsdom, where
 * every layout measurement is zero.
 *
 * Exported for its own tests: this is the whole of the arithmetic, and the only part of the
 * hook a test without a layout engine can exercise.
 */
export function nextFitFactor({ contentPx, availablePx }: FitInputs): number | undefined {
  if (!(contentPx > 0) || !(availablePx > 0)) return undefined;
  if (contentPx <= availablePx) return 1;

  const target = (availablePx / contentPx) * FIT_HEADROOM;
  if (!Number.isFinite(target)) return undefined;

  return Math.max(MIN_FIT, Math.floor(target * 1000) / 1000);
}

/**
 * Keeps `--signage-fit` on `<html>` at the largest value that lets the document fit the
 * screen. Mounted once, from `App`.
 *
 * Triggers, all three of which a display needs:
 *
 *   - **mount**, and again when the webfonts land — `font-display: block` means the first
 *     measurement happens before any text has its final metrics;
 *   - **resize**, which on a display means the panel was reconfigured or the browser left
 *     fullscreen, and in development means the window moved;
 *   - **content change**, which is what an MQTT message is. A widget going from its empty state
 *     to four rows of data changes the page height, and a display runs for weeks: a factor
 *     measured at boot is a factor measured on the least populated version of the page there
 *     will ever be.
 *
 * The observer sees this hook's own writes, which is safe because a settled pass writes the
 * value it already holds and therefore changes no box size — so there is no second callback and
 * no feedback loop. Every trigger is torn down in the cleanup (CLAUDE.md constraint 5).
 */
export function useFitToViewport(): void {
  useEffect(() => {
    const root = document.documentElement;

    /** Non-zero while a frame is queued, so n triggers in one frame cost one measurement. */
    let frame = 0;

    const settle = (): void => {
      frame = 0;

      // The design scale, unscaled. Reading `scrollHeight` immediately after forces the
      // layout, so what comes back is the page at its intended size — see the header.
      root.style.removeProperty(FIT_PROPERTY);

      let applied = 1;
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        const available = root.clientHeight;
        const ratio = nextFitFactor({ contentPx: root.scrollHeight, availablePx: available });

        // No layout to measure at all — jsdom, or the frame before the first one.
        if (ratio === undefined) return;

        /*
         * It fits. On the first pass that means it fits at the design scale and no factor is
         * wanted, which is already the state — the property was removed above. On any later
         * pass it means the factor just applied is doing its job, so **keep it**.
         *
         * Removing it here instead was a real bug, and it is worth naming because it looks
         * correct: `.signage-container` is `min-height: 100dvh`, so a page that has been scaled
         * to fit reports `scrollHeight === clientHeight` — indistinguishable from a page that
         * never needed scaling. A verification pass that reads that as "no factor needed" throws
         * away the factor, the page grows back past the fold, and the next observation applies
         * it again. Measured on `szeged_2` and `szeged_3`: 93px and 299px still below the fold
         * with `--signage-fit` reported as 1.
         */
        if (ratio >= 1) return;

        // `ratio` is measured against the *currently applied* factor, so the factor that fits is
        // the product. Passes after the first correct the non-linear remainder.
        const next = Math.max(MIN_FIT, Math.floor(applied * ratio * 1000) / 1000);
        if (Math.abs(next - applied) <= FIT_TOLERANCE) {
          if (next <= MIN_FIT && root.scrollHeight > available) reportFloor(root, available);
          return;
        }

        applied = next;
        root.style.setProperty(FIT_PROPERTY, next.toFixed(3));

        if (next <= MIN_FIT) {
          if (root.scrollHeight > available) reportFloor(root, available);
          return;
        }
      }

      // Fell out of the loop still overflowing: rare, and worth a line rather than silence.
      if (root.scrollHeight > root.clientHeight) {
        warnOnce(
          'fit-to-viewport-passes',
          '[signage] fit-to-viewport did not settle in %d passes; left at %s with %dpx still ' +
            'below the fold. Something in this layout changes height in response to the font ' +
            'size in a way a uniform scale cannot converge on.',
          MAX_PASSES,
          applied.toFixed(3),
          root.scrollHeight - root.clientHeight,
        );
      }
    };

    const schedule = (): void => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(settle);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(root);

    window.addEventListener('resize', schedule);
    // `font-display: block` means the first measurement happens before any text has its final
    // metrics, so the page is re-measured once the webfonts land. Guarded with `in` rather than
    // an optional chain because `document.fonts` is typed non-nullish and is absent in jsdom —
    // the types are right and the test environment is the reality.
    if ('fonts' in document) void document.fonts.ready.then(schedule, () => undefined);

    schedule();

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      root.style.removeProperty(FIT_PROPERTY);
    };
  }, []);
}

/**
 * The one case this hook cannot fix: a config that still does not fit at the size floor. Said
 * once, with the numbers, because the remedy is a config change and the only person who can
 * make it needs to know which display and by how much.
 */
function reportFloor(root: HTMLElement, available: number): void {
  warnOnce(
    'fit-to-viewport-floor',
    '[signage] this config does not fit the screen even at the %d%% size floor: %dpx of ' +
      'document in %dpx of viewport, so %dpx is below the fold and a display does not ' +
      'scroll. There is too much on this screen for its size — the fix is the config.',
    Math.round(MIN_FIT * 100),
    root.scrollHeight,
    available,
    root.scrollHeight - available,
  );
}
